/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import type { HrTime } from '@opentelemetry/api';
import type { InstrumentationScope } from '@opentelemetry/core';
import type { MetricCollectOptions } from '../export/MetricProducer';
import type { MetricData, ScopeMetrics } from '../export/MetricData';
import type { InstrumentDescriptor } from '../InstrumentDescriptor';
import { createInstrumentDescriptorWithView } from '../InstrumentDescriptor';
import { Meter } from '../Meter';
import type { Maybe } from '../utils';
import { AsyncMetricStorage } from './AsyncMetricStorage';
import type { MeterProviderSharedState } from './MeterProviderSharedState';
import type { MetricCollectorHandle } from './MetricCollector';
import { MetricStorageRegistry } from './MetricStorageRegistry';
import { MultiMetricStorage } from './MultiWritableMetricStorage';
import { ObservableRegistry } from './ObservableRegistry';
import { SyncMetricStorage } from './SyncMetricStorage';
import type { Accumulation, Aggregator } from '../aggregator/types';
import type { IAttributesProcessor } from '../view/AttributesProcessor';
import { createNoopAttributesProcessor } from '../view/AttributesProcessor';
import type { MetricStorage } from './MetricStorage';

/**
 * An internal record for shared meter provider states.
 */
export class MeterSharedState {
  metricStorageRegistry = new MetricStorageRegistry();
  observableRegistry = new ObservableRegistry();
  meter: Meter;
  private _meterProviderSharedState: MeterProviderSharedState;
  private _instrumentationScope: InstrumentationScope;

  constructor(
    meterProviderSharedState: MeterProviderSharedState,
    instrumentationScope: InstrumentationScope
  ) {
    this.meter = new Meter(this);
    this._meterProviderSharedState = meterProviderSharedState;
    this._instrumentationScope = instrumentationScope;
  }

  registerMetricStorage(descriptor: InstrumentDescriptor) {
    const storages = this._registerMetricStorage(descriptor, SyncMetricStorage);

    if (storages.length === 1) {
      return storages[0];
    }
    return new MultiMetricStorage(storages);
  }

  registerAsyncMetricStorage(descriptor: InstrumentDescriptor) {
    const storages = this._registerMetricStorage(
      descriptor,
      AsyncMetricStorage
    );

    return storages;
  }

  /**
   * @param collector opaque handle of {@link MetricCollector} which initiated the collection.
   * @param collectionTime the HrTime at which the collection was initiated.
   * @param options options for collection.
   * @returns the list of metric data collected.
   */
  async collect(
    collector: MetricCollectorHandle,
    collectionTime: HrTime,
    options?: MetricCollectOptions
  ): Promise<ScopeMetricsResult | null> {
    /**
     * 1. Call all observable callbacks first.
     * 2. Collect metric result for the collector.
     */
    const errors = await this.observableRegistry.observe(
      collectionTime,
      options?.timeoutMillis
    );
    const storages = this.metricStorageRegistry.getStorages(collector);

    // prevent more allocations if there are no storages.
    if (storages.length === 0) {
      return null;
    }

    const metricDataList: MetricData[] = [];
    storages.forEach(metricStorage => {
      const metricData = metricStorage.collect(collector, collectionTime);

      if (metricData != null) {
        metricDataList.push(metricData);
      }
    });

    // skip this scope if no data was collected (storage created, but no data observed)
    if (metricDataList.length === 0) {
      return { errors };
    }

    return {
      scopeMetrics: {
        scope: this._instrumentationScope,
        metrics: metricDataList,
      },
      errors,
    };
  }

  private _registerMetricStorage<
    MetricStorageType extends MetricStorageConstructor,
    R extends InstanceType<MetricStorageType>,
  >(
    descriptor: InstrumentDescriptor,
    MetricStorageType: MetricStorageType
  ): R[] {
    const views = this._meterProviderSharedState.viewRegistry.findViews(
      descriptor,
      this._instrumentationScope
    );
    let storages = views.map(view => {
      const viewDescriptor = createInstrumentDescriptorWithView(
        view,
        descriptor
      );
      const compatibleStorage =
        this.metricStorageRegistry.findOrUpdateCompatibleStorage<R>(
          viewDescriptor
        );
      if (compatibleStorage != null) {
        return compatibleStorage;
      }
      const aggregator = view.aggregation.createAggregator(viewDescriptor);
      const viewStorage = new MetricStorageType(
        viewDescriptor,
        aggregator,
        view.attributesProcessor,
        this._meterProviderSharedState.metricCollectors,
        view.aggregationCardinalityLimit
      ) as R;
      this.metricStorageRegistry.register(viewStorage);
      return viewStorage;
    });

    // Fallback to the per-collector aggregations if no view is configured for the instrument.
    if (storages.length === 0) {
      const perCollectorAggregations =
        this._meterProviderSharedState.selectAggregations(descriptor.type);
      const collectorStorages = perCollectorAggregations.map(
        ([collector, aggregation]) => {
          const compatibleStorage =
            this.metricStorageRegistry.findOrUpdateCompatibleCollectorStorage<R>(
              collector,
              descriptor
            );
          if (compatibleStorage != null) {
            return compatibleStorage;
          }

          const aggregator = aggregation.createAggregator(descriptor);
          const cardinalityLimit = collector.selectCardinalityLimit(
            descriptor.type
          );
          const storage = new MetricStorageType(
            descriptor,
            aggregator,
            createNoopAttributesProcessor(),
            [collector],
            cardinalityLimit
          ) as R;
          this.metricStorageRegistry.registerForCollector(collector, storage);
          return storage;
        }
      );
      storages = storages.concat(collectorStorages);
    }

    return storages;
  }
}

interface ScopeMetricsResult {
  scopeMetrics?: ScopeMetrics;
  errors: unknown[];
}

interface MetricStorageConstructor {
  new (
    instrumentDescriptor: InstrumentDescriptor,
    aggregator: Aggregator<Maybe<Accumulation>>,
    attributesProcessor: IAttributesProcessor,
    collectors: MetricCollectorHandle[],
    aggregationCardinalityLimit?: number
  ): MetricStorage;
}
